import { fetcher } from "@certn/api";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Icon,
  Loader,
  Modal,
  ModalTrigger,
  PrimaryButton,
  RadioGroup,
  Radio,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { JsonSchema4, JsonSchema7 } from "@jsonforms/core";
import { useCallback, useContext, useState } from "react";
import { FormValidationContext } from "react-aria-components";
import { Trans, useTranslation } from "react-i18next";

import { useApplicationFormContext } from "../../ApplicationFormContext";
import { useHandleChange } from "../../utils";

type Props = {
  schema: JsonSchema4 | JsonSchema7 | undefined;
  incomeVerificationSkipped: boolean;
};

type IFrameStateType = {
  flinksLoading: boolean;
  flinksUrl: string;
  flinksError: boolean;
  countrySelection: string;
  showFlinksModal: boolean;
};

export function FlinksIFrameModal(props: Props) {
  const { schema, incomeVerificationSkipped } = props;
  const { t, i18n } = useTranslation();
  const baseHeight = "720px";
  const { caseId } = useApplicationFormContext();
  const handleChange = useHandleChange();
  const formValidation = useContext(FormValidationContext);
  const submitErrors = formValidation["income_verification_country"];
  const language = i18n.language.split("-")[0];

  const [iFrameState, setIFrameState] = useState<IFrameStateType>({
    flinksLoading: true,
    flinksUrl: "",
    flinksError: false,
    countrySelection: "",
    showFlinksModal: false,
  });

  const getFlinksUrl = useCallback(
    async (countrySelection: string) => {
      setIFrameState((currValues) => ({ ...currValues, flinksError: false }));
      try {
        const { flinks_url } = await fetcher.request<{ flinks_url: string }>(
          `/api/internal/applicant/case/${caseId}/income-verification/url/`,
          {
            method: "POST",
            body: JSON.stringify({
              case_id: caseId,
              bank_country: countrySelection,
              language,
            }),
          }
        );
        setIFrameState((currValues) => ({
          ...currValues,
          flinksUrl: flinks_url,
        }));
      } catch (e) {
        console.error("Error getting flinks Url:", e);
        setIFrameState((currValues) => ({ ...currValues, flinksError: true }));
      }
    },
    [caseId, language]
  );

  const countryOptions = schema?.properties?.income_verification_country?.oneOf;

  return (
    <>
      <ModalTrigger
        trigger={
          <BasicButton
            isDisabled={iFrameState.flinksError || incomeVerificationSkipped}
            onPress={() => {
              handleChange("income_verification_country", null);
              handleChange(
                "income_verification_additional_information",
                undefined
              );
            }}
          >
            {t("Start Verification")}
          </BasicButton>
        }
      >
        <Modal scroll="viewport" style={{ width: "100vw" }}>
          <Dialog>
            {({ close }) => (
              <>
                {!iFrameState.showFlinksModal && (
                  <>
                    <DialogHeader testID="flinks-modal">
                      {t("Bank account location")}
                    </DialogHeader>
                    <DialogContent>
                      {iFrameState.flinksError ? (
                        <Text
                          style="global/body-primary"
                          testID="income-verification-modal-error"
                        >
                          <Trans>
                            Unable to start session. Please contact support.
                          </Trans>
                        </Text>
                      ) : (
                        <>
                          <Text>
                            {t(
                              "Please select the location of the bank account your income is deposited in."
                            )}
                          </Text>
                          <RadioGroup
                            className={styles.radioGroupMargin}
                            aria-label="aria-label"
                            name="name"
                            onChange={(value) => {
                              setIFrameState((currValues) => ({
                                ...currValues,
                                countrySelection: value,
                              }));
                            }}
                            isRequired
                            children={
                              countryOptions &&
                              countryOptions.map((item, index) => {
                                return (
                                  <Radio
                                    isCard
                                    key={index}
                                    value={item.const}
                                    testID={`${item.const}-radio-selection`}
                                  >
                                    {item.title ?? item.const}
                                  </Radio>
                                );
                              })
                            }
                            testID="test-id"
                          />
                        </>
                      )}
                    </DialogContent>
                    <DialogFooter>
                      {iFrameState.flinksError ? (
                        <PrimaryButton
                          onPress={() => {
                            close();
                            setIFrameState((currValues) => ({
                              ...currValues,
                              countrySelection: "",
                              flinksLoading: true,
                              flinksError: false,
                              showFlinksModal: false,
                            }));
                            handleChange("income_verification_country", null);
                          }}
                        >
                          <Trans>Close</Trans>
                        </PrimaryButton>
                      ) : (
                        <PrimaryButton
                          isDisabled={!iFrameState.countrySelection}
                          onPress={() => {
                            getFlinksUrl(
                              iFrameState.countrySelection
                                ? iFrameState.countrySelection
                                : ""
                            );
                            handleChange(
                              "income_verification_country",
                              iFrameState.countrySelection
                            );
                            setIFrameState((currValues) => ({
                              ...currValues,
                              showFlinksModal: true,
                            }));
                          }}
                        >
                          <Trans>Continue</Trans>
                        </PrimaryButton>
                      )}
                    </DialogFooter>
                  </>
                )}
                {iFrameState.showFlinksModal && (
                  <>
                    <DialogContent>
                      <>
                        <iframe
                          onLoad={() => {
                            setIFrameState((currValues) => ({
                              ...currValues,
                              flinksLoading: false,
                            }));
                          }}
                          src={iFrameState.flinksUrl}
                          id="flinks"
                          title="Flinks"
                          data-testid="flinks-modal"
                          allow="camera"
                          frameBorder="0"
                          style={{
                            height: baseHeight,
                            width: "100%",
                            margin: "auto",
                          }}
                        />
                        {iFrameState.flinksLoading && <Loader overlay />}
                      </>
                    </DialogContent>
                    <DialogFooter>
                      <PrimaryButton
                        onPress={() => {
                          close();
                          setIFrameState((currValues) => ({
                            ...currValues,
                            countrySelection: "",
                            flinksLoading: true,
                            flinksError: false,
                            showFlinksModal: false,
                          }));
                          handleChange("income_verification_country", null);
                        }}
                      >
                        <Trans>Close</Trans>
                      </PrimaryButton>
                    </DialogFooter>
                  </>
                )}
              </>
            )}
          </Dialog>
        </Modal>
      </ModalTrigger>
      {submitErrors && (
        <div className={styles.errorContainer}>
          <Icon name="error" color="icon-danger" fill />
          <Text color="text-danger" style="form/body-secondary">
            {submitErrors}
          </Text>
        </div>
      )}
    </>
  );
}

const styles = {
  radioGroupMargin: css({
    marginTop: "var(--space-3)",
  }),
  errorContainer: css({ display: "flex", columnGap: "var(--space-1)" }),
};
